import { usePostHog } from "posthog-js/react"

import { mdxComponents } from "@/components/content/mdx-overrides"
import { MdxProse } from "@/components/content/prose"
import { authorUrl, getBlogAuthors } from "@/lib/blog-authors"
import type { ContentEntry } from "@/lib/content-resolver"
import { SiteRebellionSignup } from "@/routes/-components/site/SiteRebellionSignup"
import { SiteSection } from "@/routes/-components/site/SiteSection"

import {
  getPostCategory,
  getPostFeatureImage,
  getPostStrip,
  getPostTitle,
  isFeatureImageHidden,
} from "./blogHelpers"
import { BlogCategoryBadge, BlogFeatureImage, BlogPostCard, BlogPostMeta } from "./BlogPostParts"

interface BlogArticlePageProps {
  post: ContentEntry
  posts: ContentEntry[]
}

export function BlogArticlePage({ post, posts }: BlogArticlePageProps) {
  const posthog = usePostHog()
  const relatedPosts = getPostStrip(posts, post.slug)
  const featureImage = isFeatureImageHidden(post) ? undefined : getPostFeatureImage(post)

  function trackPostClick(relatedPost: ContentEntry) {
    posthog.capture("blog_post_clicked", {
      slug: relatedPost.slug,
      title: getPostTitle(relatedPost),
    })
  }

  return (
    <>
      {/* React hoists these to head. Router head metadata deduplicates by property,
          which would drop all but one author on coauthored articles. */}
      {getBlogAuthors(post.frontmatter["author"]).map((author) => (
        <meta key={author.slug} property="article:author" content={authorUrl(author)} />
      ))}
      <SiteSection innerClassName="flex flex-col items-center gap-10 px-6 py-10 md:px-20 md:pb-20">
        {featureImage && (
          <div className="aspect-7/4 w-full max-w-200 overflow-hidden rounded-lg border border-subtle bg-(--neutral-150) dark:bg-neutral-900">
            <BlogFeatureImage
              post={post}
              width={800}
              height={457}
              priority
              pictureClassName="block size-full"
            />
          </div>
        )}
        <article className="mx-auto flex w-full max-w-180 flex-col gap-8">
          <header className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <BlogPostMeta post={post} linkAuthors />
              <h1 className="text-site-title-lg font-headline text-default">
                {getPostTitle(post)}
              </h1>
            </div>
            <BlogCategoryBadge category={getPostCategory(post)} />
          </header>
          <div className="text-site-body font-sans text-muted [&_:not(pre)>code]:whitespace-nowrap [&_p]:text-pretty">
            <MdxProse>
              <post.Content components={mdxComponents} />
            </MdxProse>
          </div>
        </article>
      </SiteSection>
      {relatedPosts.length > 0 && (
        <SiteSection innerClassName="grid md:grid-cols-3">
          {relatedPosts.map((relatedPost) => (
            <BlogPostCard
              key={relatedPost.slug}
              post={relatedPost}
              onClick={() => trackPostClick(relatedPost)}
            />
          ))}
        </SiteSection>
      )}
      <SiteRebellionSignup />
    </>
  )
}
